<script setup lang="ts">
import { utc } from '@date-fns/utc';
import XMarkIcon from '@fortawesome/fontawesome-free/svgs/solid/xmark.svg?component';
import { useQuery } from '@tanstack/vue-query';
import { format } from 'date-fns';
import { noop } from 'lodash-es';
import { computed } from 'vue';
import { useRouter } from 'vue-router';

import { getNextRehearsal } from '@/api/booking';
import LocaleSwitch from '@/components/LocaleSwitch.vue';
import Modal from '@/core/Modal.vue';
import { useAuthStore } from '@/stores/auth';
import { getNaiveToday } from '@/utils/booking';

const authStore = useAuthStore();
const router = useRouter();

defineProps<{
  back: () => Promise<unknown>;
  noLocaleSwitch?: boolean;
}>();

const { data: rehearsal } = useQuery(
  getNextRehearsal.query(
    computed(() => authStore.uid),
    getNaiveToday(),
  ),
);
</script>

<template>
  <Modal
    :class="$style.root"
    :modifier="['dialog-drawer-left', 'dialog-scrollable']"
    :disabled="authStore.loading"
    tag="nav"
    @hide="back"
  >
    <template #header>
      <button
        type="button"
        class="btn btn-lg btn-link link-body"
        :title="$t`Close`"
        @click="back()"
      >
        <XMarkIcon class="inline-fill" /></button
    ></template>

    <template v-if="authStore.uid">
      <p v-if="rehearsal === undefined" class="my-4 text-end text-pre-wrap">
        {{
          $t`${authStore.name || $t`Guest`},\nwe are looking for\nupcoming rehearsals`
        }}
      </p>

      <template v-else-if="rehearsal">
        <p class="my-4 text-end text-pre-wrap">
          {{
            $t`${authStore.name || $t`Guest`},\ngood vibes on your next rehearsal!`
          }}
        </p>

        <p :class="$style.date">
          <span
            >{{ format(rehearsal.start, 'HH:mm', { in: utc }) }}<br />{{
              format(rehearsal.start, 'MMMM', { in: utc })
            }}</span
          >
          <span>{{ format(rehearsal.start, 'd', { in: utc }) }}</span>
          <span>{{ format(rehearsal.start, 'EEEE', { in: utc }) }}</span>
        </p>
      </template>

      <p v-else class="my-4 text-end text-pre-wrap">
        {{
          $t`${authStore.name || $t`Guest`},\nyou have no upcoming\nrehearsal`
        }}
      </p>

      <ul class="list-unstyled">
        <li
          v-for="page in [
            { title: $t`My\nrehearsals`, url: '/booking' },
            { title: $t`My profile`, url: '/me' },
          ]"
          :key="page.url"
        >
          <RouterLink
            :to="page.url"
            class="btn btn-intro btn-link link-body btn-underline text-pre-wrap"
            >{{ page.title }}</RouterLink
          >
        </li>
      </ul>

      <template v-if="authStore.isAdmin">
        <h2>Admin</h2>

        <ul class="list-unstyled">
          <li
            v-for="page in [
              { title: 'Bookings', url: '/admin' },
              { title: 'Room booking', url: '/admin/bookings/room' },
              { title: 'Equip booking', url: '/admin/bookings/equip' },
              { title: 'Equipment', url: '/admin/equipment' },
              { title: 'Promos', url: '/admin/promos' },
              { title: 'Pricing', url: '/admin/pricing' },
              { title: 'Users', url: '/admin/users' },
              {
                title: 'I18N',
                url: 'https://github.com/rucebee/cs-web/edit/main/locales/de-DE.txt',
              },
              {
                title: 'Deploy',
                url: 'https://github.com/rucebee/cs-web/actions/workflows/deploy.yml',
              },
            ]"
            :key="page.url"
          >
            <a
              v-if="page.url[0] === '/'"
              :href="page.url"
              class="btn btn-intro btn-link link-body btn-underline text-pre-wrap"
              @click="
                back()
                  .then(() => router.push(page.url))
                  .catch(noop)
              "
              >{{ page.title }}</a
            >

            <a
              v-else
              :href="page.url"
              class="btn btn-intro btn-link link-body btn-underline text-pre-wrap"
              @click.capture="back()"
              >{{ page.title }}</a
            >
          </li>
        </ul>
      </template>
    </template>

    <template v-else>
      <div class="my-4 text-end">
        {{ $t`Hi! Please, sign in to see your rehearsals` }}
      </div>

      <button
        type="button"
        class="btn btn-intro btn-link link-body btn-underline"
        :disabled="authStore.loading"
        @click="
          async () => {
            await back();
            await router.push('/auth');
          }
        "
      >
        <span>{{ $t`Sign in` }}</span>
      </button>
    </template>

    <template v-if="!noLocaleSwitch" #footer>
      <LocaleSwitch />
    </template>
  </Modal>
</template>

<style lang="scss" module>
.root {
  --#{$prefix}body-bg: #{$dark};
  --#{$prefix}body-color: #{$body-bg};
  --#{$prefix}body-color-rgb: #{to-rgb($body-bg)};

  h2 {
    @include intro-font;

    font-size: $font-size-intro-sm;
    border-bottom: var(--bs-border-color) solid $border-width;
  }

  :global {
    .modal {
      --#{$prefix}modal-width: 16rem;
      --#{$prefix}modal-border-width: 0;
      --#{$prefix}modal-header-border-width: 0;
      --#{$prefix}modal-footer-border-width: 0;
    }

    .modal-header {
      padding: 0;
    }

    .modal-footer {
      padding-top: 0;
      padding-bottom: 0;
    }
  }

  .LocaleSwitch {
    display: flex;
    gap: $spacer * 0.5;
    justify-content: flex-end;
  }

  .LocaleSwitchBtn {
    @include intro-font;

    --#{$prefix}btn-padding-x: 0;
    --#{$prefix}btn-font-size: #{$font-size-intro};
  }

  .date {
    margin-top: 2rem;
    text-align: right;

    :nth-child(1) {
      font-size: $font-size-sm;
      display: inline-block;
      text-transform: uppercase;
    }

    :nth-child(2) {
      font-size: 5rem;
      line-height: 1;
      display: inline-block;
      font-family: Intro, sans-serif;
      font-weight: 700;
      letter-spacing: 0.1em;
    }

    :nth-child(3) {
      display: block;
      text-align: center;
      letter-spacing: 0.62em;
    }
  }
}
</style>
